import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { GraphqlResolverInput } from 'global';
import { useTimeZone } from 'hooks/useTimeZone';
import { useOptimisticState } from 'hooks/useOptimistic';
import { CalendarEventsInput } from '@trnsact/trnsact-shared-types';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { useEventIdParam } from '../../hooks';
import { CalendarMapper } from '../../mapper';
import { CalendarEventMapper } from './mapper';
import { ACTIVITY_PARENT_ROUTES } from './constants';
import { calendarApi, useCalendar } from '../../api';
import { CalendarSkeleton } from './CalendarSkeleton';
import { DealActivityAPINameSpace } from '../../../api/types';
import { CalendarEvent, CalendarEventsWithActivitiesResponse, CalendarConnectorProps } from './types';

export const CalendarConnector = ({
  period,
  children,
  dateRange,
  userProfileId,
  includeOwnEvents = true,
}: CalendarConnectorProps) => {
  const history = useHistory();
  const { openEvent } = useEventIdParam();
  const { handleReDateEvent } = useCalendar();
  const { timeZoneOffsetMinutes, timeZone } = useTimeZone();

  const { data, previousData, error, loading } = useQuery<
    DealActivityAPINameSpace<CalendarEventsWithActivitiesResponse>,
    GraphqlResolverInput<CalendarEventsInput>
  >(calendarApi.events.queries.listWithActivities, {
    variables: {
      input: {
        period,
        timeZone,
        includeOwnEvents,
        timeZoneOffsetMinutes,
        ...dateRange,
        ...(userProfileId && { userProfileId }),
      },
    },
  });

  const result = (data ?? previousData)?.dealActivity;
  const eventsList = result?.events?.list || [];
  const activitiesList = result?.activities?.list || [];

  const serverEvents = [
    ...CalendarEventMapper.mapToCalendarEvent(eventsList),
    ...CalendarEventMapper.mapActivityToCalendarEvent(activitiesList),
  ];

  const [events, setEvents] = useOptimisticState<CalendarEvent[]>(serverEvents);

  useEffect(() => {
    setEvents(serverEvents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CalendarMapper.getEventsSignature(serverEvents)]);

  const handleSelectEvent = (event: CalendarEvent) => {
    if (!event.activity) {
      openEvent(event.id);
      return;
    }

    const { parentId, parentType } = event.activity;

    if (parentId && parentType) history.push(`${ACTIVITY_PARENT_ROUTES[parentType]}/${parentId}`);
  };

  const isCalendarEvent = (event: CalendarEvent) => !event.activity;

  const handleEventDrop = async (dto: EventInteractionArgs<CalendarEvent>) => {
    const movedEvents = CalendarMapper.moveEventInList(events, dto);
    const reDateEvent = CalendarMapper.mapDtoToReDate(dto);

    await setEvents(
      handleReDateEvent({ events: [reDateEvent] }, api => ({
        awaitRefetchQueries: true,
        refetchQueries: [api.events.queries.listWithActivities],
      })).then(result => (result ? movedEvents : events)),
      movedEvents
    );
  };

  return (
    <RenderWithFallback error={error} isLoading={loading && !eventsList.length} fallBack={<CalendarSkeleton />}>
      {children({
        events,
        onEventDrop: handleEventDrop,
        onSelectEvent: handleSelectEvent,
        draggableAccessor: isCalendarEvent,
        resizableAccessor: isCalendarEvent,
      })}
    </RenderWithFallback>
  );
};
